/**
 * Hero backdrop theme toggle.
 *
 * Set in `.env.local` (restart dev server after changing):
 *
 * | Env value | Overlay tokens on home        | CSS class              | Hero look                         | Nav at top   |
 * |-----------|-------------------------------|------------------------|-----------------------------------|--------------|
 * | `dark`    | Spartan (default)             | cc-hero--theme-dark    | Light surface, dark copy          | Light links  |
 * | `light`   | Dark scrim                    | cc-hero--theme-light   | Dark overlay, white body text     | Dark nav     |
 *
 * **Production home hero variant** (`NEXT_PUBLIC_HOME_HERO_VARIANT`, restart after change):
 *
 * | Variant     | Env value              | Backdrop / layout                         | Overlay | Nav   |
 * |-------------|------------------------|-------------------------------------------|---------|-------|
 * | Gallery petrol (default) | unset, `gallery-petrol` | centered stack + petrol backdrop, scrim ×0.3 | `light` (dark scrim) | dark |
 * | AI face     | `ai-face`, `default` | `HERO_BACKDROP_IMAGES` + default layout | `light` when `NEXT_PUBLIC_HERO_THEME` unset* | dark  |
 * | Acropolis   | `acropolis`            | corner-anchor + full logo band (preset)   | Spartan (`dark`) | light |
 *
 * \* For `ai-face`, optional `NEXT_PUBLIC_HERO_THEME`=`light`|`dark` swaps env slot artwork + overlay.
 * `HERO_BACKDROP_IMAGES` remains for dev gallery env slots and service heroes.
 *
 * Naming is about the CSS theme token, not the visual brightness of the hero.
 * Valid values: "light" | "dark". Defaults to "dark" when unset or invalid.
 *
 * **Dev lab (`/dev/hero`):** `cc_dev_hero` query param and `cc-dev-preset` cookie can set
 * `heroTheme` for homepage previews only (via `getDevLayoutOverrides()` → `getHeroTheme(devOverride)`).
 * That override does not change production builds; it is ignored when `NODE_ENV=production`.
 * Env `NEXT_PUBLIC_HERO_THEME` still defines the shipped default when no dev override is active.
 */
export type HeroTheme = "light" | "dark";

/**
 * Navbar chrome at the top of the viewport (`data-cc-nav`, `CcNavbar` `hasDarkHero`).
 * Describes the **nav bar** (light = dark link color on a light pill), not `HeroTheme` CSS tokens.
 */
export type HeroNavTone = "light" | "dark";

/** Production home uses compact (left-aligned narrow band at bottom). */
export type HeroLogoBandLayout = "compact" | "full";

export type HeroBackdropLayout = "default" | "corner-anchor" | "centered";

/** Shipped homepage hero artwork preset — flip env to restore prior home looks. */
export type ProductionHomeHeroVariant = "gallery-petrol" | "ai-face" | "acropolis";

/** Change this constant when you want a repo-wide default without env vars. */
export const HERO_THEME_FALLBACK: HeroTheme = "dark";

/** Production home hero artwork (true RGBA PNG — keep under `public/assets/imgs/header/`). */
export const HERO_ACROPOLIS_BACKDROP_SRC = "/assets/imgs/header/hero-acropolis.png";

export const HERO_GALLERY_PETROL_BACKDROP_SRC =
  "/assets/imgs/header/hero-gallery-petrol.jpg";

export const HOME_HERO_AVIF_SRC_SET = [480, 768, 1024]
  .map(width => `/assets/imgs/header/home-hero/petrol-${width}.avif ${width}w`).join(", ");
export const HOME_HERO_WEBP_SRC_SET = [480, 768, 1024]
  .map(width => `/assets/imgs/header/home-hero/petrol-${width}.webp ${width}w`).join(", ");

/** Services overview (`/services`) inset hero artwork. */
export const HERO_SERVICES_OVERVIEW_BACKDROP_SRC =
  "/assets/imgs/header/hero-services-hands.jpg";

/** Projects overview (`/projects`) inset hero artwork. */
export const HERO_PROJECTS_OVERVIEW_BACKDROP_SRC =
  "/assets/imgs/dev-library/sculptor-hands-marble-bust.jpg";

/** Blog list (`/blog`) inset hero artwork. */
export const HERO_BLOG_BACKDROP_SRC =
  "/assets/imgs/dev-library/roman-trireme-moonlight.jpg";

export const HERO_BACKDROP_IMAGES: Record<HeroTheme, string> = {
  light: "/assets/imgs/header/ai-generated-human-face.webp",
  dark: "/assets/imgs/header/hero-marble-tech.jpg",
};

/** Resolved production home hero (backdrop, layout, overlay, nav). */
export type ProductionHomeHeroVisual = {
  backdropSrc: string;
  backdropLayout: HeroBackdropLayout;
  logoBandLayout: HeroLogoBandLayout;
  /** Overlay CSS token (`cc-hero--theme-*`). Spartan Acropolis uses `"dark"` = light hero surface. */
  overlayTheme: HeroTheme;
  /** Navbar at page top while over the hero — Acropolis home uses light nav (dark links). */
  heroNavTone: HeroNavTone;
  /** Backdrop scrim multiplier (`--cc-hero-backdrop-scrim-opacity`); omit for full strength (1). */
  backdropScrimStrength?: number;
};

export const PRODUCTION_HOME_HERO_VARIANT_FALLBACK: ProductionHomeHeroVariant =
  "gallery-petrol";

/** Acropolis corner-anchor + Spartan overlay — set `NEXT_PUBLIC_HOME_HERO_VARIANT=acropolis`. */
export const PRODUCTION_HOME_HERO_ACROPOLIS: ProductionHomeHeroVisual = {
  backdropSrc: HERO_ACROPOLIS_BACKDROP_SRC,
  backdropLayout: "corner-anchor",
  logoBandLayout: "full",
  overlayTheme: "dark",
  heroNavTone: "light",
};

/** Centered gallery petrol — matches `/dev/hero` gallery-petrol-centered (scrim slider 30). */
export const PRODUCTION_HOME_HERO_GALLERY_PETROL: ProductionHomeHeroVisual = {
  backdropSrc: HERO_GALLERY_PETROL_BACKDROP_SRC,
  backdropLayout: "centered",
  logoBandLayout: "compact",
  overlayTheme: "light",
  heroNavTone: "dark",
  backdropScrimStrength: 0.3,
};

export function getProductionHomeHeroVariant(): ProductionHomeHeroVariant {
  const variant = process.env.NEXT_PUBLIC_HOME_HERO_VARIANT;

  if (variant === "acropolis") {
    return "acropolis";
  }

  if (
    variant === "gallery-petrol" ||
    variant === "gallery-petrol-centered" ||
    variant === "petrol-centered"
  ) {
    return "gallery-petrol";
  }

  if (variant === "ai-face" || variant === "default") {
    return "ai-face";
  }

  return PRODUCTION_HOME_HERO_VARIANT_FALLBACK;
}

/** AI-face home defaults to the light env slot (face + dark scrim) when theme env is unset. */
function getProductionAiFaceOverlayTheme(): HeroTheme {
  const envTheme = process.env.NEXT_PUBLIC_HERO_THEME;

  if (envTheme === "light" || envTheme === "dark") {
    return envTheme;
  }

  return "light";
}

function getProductionAiFaceHomeHeroVisual(): ProductionHomeHeroVisual {
  const overlayTheme = getProductionAiFaceOverlayTheme();

  return {
    backdropSrc: HERO_BACKDROP_IMAGES[overlayTheme],
    backdropLayout: "default",
    logoBandLayout: "compact",
    overlayTheme,
    heroNavTone: heroNavToneFromOverlayTheme(overlayTheme),
  };
}

export function getProductionHomeHeroVisual(): ProductionHomeHeroVisual {
  const variant = getProductionHomeHeroVariant();

  if (variant === "acropolis") {
    return PRODUCTION_HOME_HERO_ACROPOLIS;
  }

  if (variant === "gallery-petrol") {
    return PRODUCTION_HOME_HERO_GALLERY_PETROL;
  }

  return getProductionAiFaceHomeHeroVisual();
}

export function getHeroTheme(devOverride?: HeroTheme | null): HeroTheme {
  if (devOverride === "light" || devOverride === "dark") {
    return devOverride;
  }

  const envTheme = process.env.NEXT_PUBLIC_HERO_THEME;

  if (envTheme === "light" || envTheme === "dark") {
    return envTheme;
  }

  return HERO_THEME_FALLBACK;
}

export function getHeroBackdropImage(devOverride?: HeroTheme | null): string {
  return HERO_BACKDROP_IMAGES[getHeroTheme(devOverride)];
}

/** Maps overlay token → nav bar tone (inverse of scrim brightness). */
export function heroNavToneFromOverlayTheme(theme: HeroTheme): HeroNavTone {
  return theme === "light" ? "dark" : "light";
}

/** `CcNavbar` `hasDarkHero` / dark nav pill when tone is `"dark"`. */
export function heroNavToneUsesDarkNavBar(tone: HeroNavTone): boolean {
  return tone === "dark";
}

/** Face backdrop (env "light") uses white hero text — nav needs light links on dark overlay. */
export function heroUsesDarkNav(devOverride?: HeroTheme | null): boolean {
  return heroNavToneUsesDarkNavBar(heroNavToneFromOverlayTheme(getHeroTheme(devOverride)));
}

/** Homepage SSR nav fallback: production variant nav tone, or dev overlay override. */
export function getProductionHomeHeroNavTone(devOverride?: HeroTheme | null): HeroNavTone {
  if (devOverride === "light" || devOverride === "dark") {
    return heroNavToneFromOverlayTheme(devOverride);
  }
  return getProductionHomeHeroVisual().heroNavTone;
}

export function productionHomeUsesDarkNavBar(devOverride?: HeroTheme | null): boolean {
  return heroNavToneUsesDarkNavBar(getProductionHomeHeroNavTone(devOverride));
}

/** Value for `data-cc-nav` on the homepage hero — drives CcNavbar section theming. */
export function getHeroNavDataAttr(devOverride?: HeroTheme | null): "dark" | "light" {
  return getHeroNavDataAttrFromTone(heroNavToneFromOverlayTheme(getHeroTheme(devOverride)));
}

export function getHeroNavDataAttrFromTone(tone: HeroNavTone): HeroNavTone {
  return tone;
}
